import { Button, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateCandidateModal from "../components/CreateCandidateModal";
import CustomTable from "../components/CustomTable";
import DataSelect from "../components/DataInputs/DataSelect";
import PeriodSelector from "../components/DataInputs/DataPeriodSelector";
import DataTextField from "../components/DataInputs/DataTextField";
import FilterBase from "../components/Filter";
import usePagination, { InfinityPagination } from "../hooks/Pagination";
import {
	CandidateFilter,
	CandidateSearch,
	CandidateTable,
} from "../interfaces/Candidate";
import {
	ArrayContainer,
	Filter,
	Pagination,
	ResponseContainer,
	Sort,
} from "../interfaces/RequestBase";
import { TableTemplate } from "../interfaces/Table";
import {
	GetCandidates,
	GetManagers,
	GetStatuses,
} from "../requester/functions";
import getFilter from "../utilities/filter";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { ManagerTable } from "../interfaces/Manager";
import dateToHuman from "../utilities/date";
import { Status } from "../interfaces/Status";
import { GenderObjects } from "../Enums/Gender";
import { getFullName } from "../utilities/string";

interface IProps {}

const CandidatesPage = (props: IProps) => {
	const navigate = useNavigate();

	const [candidates, setCandidates] = useState<CandidateTable[]>([]);

	const [managers, setManagers] = useState<{ id: number; name: string }[]>([]);
	const [status, setStatus] = useState<Status[]>([]);

	const [openModal, setOpenModal] = useState<boolean>(false);
	const [pagination, setPage, setPageSize] = usePagination();
	const [totalCount, setTotalCount] = useState<number>(0);
	const [filter, setFilter] = useState<CandidateFilter>({});
	const [search, setSearch] = useState<CandidateSearch>({});
	const [sort, setSort] = useState<Sort>({});

	const rows: TableTemplate = {
		data: [
			{
				title: "ФИО",
				sortKey: "last_name",
				render: (item: CandidateTable) =>
					getFullName(item.first_name, item.last_name, item.patronymic),
			},
			{
				title: "Зарегистрирован",
				sortKey: "created",
				render: (item) => dateToHuman(item.created),
			},
			{
				title: "Отвественный",
				sortKey: "manager.last_name",
				render: (item: CandidateTable) => item.manager?.full_name || "Нет",
			},
			{
				title: "Телефон",
				key: "phone",
				sortKey: "phone",
			},
			{
				title: "Почта",
				key: "email",
				sortKey: "email",
			},
			{
				title: "",
				render: (item: CandidateTable) => (
					<IconButton
						onClick={() => {
							navigate(`/candidate/${item.id}`);
						}}
					>
						<AccountBoxIcon />
					</IconButton>
				),
			},
		],
	};

	useEffect(() => {
		GetManagers(
			{
				pagination: InfinityPagination,
			},
			(resp: ResponseContainer<ArrayContainer<ManagerTable>>) => {
				if (resp.data) {
					setManagers(
						resp.data.data.map((m) => ({
							id: m.id,
							name: m.first_name,
						}))
					);
				}
			}
		);
		GetStatuses({ filter_by: { type: "candidate" } }, (resp) => {
			if (resp.status === "ok" && resp.data) {
				setStatus(resp.data);
			}
		});
	}, []);

	const getCandidatesExport = (callback: any) => {
		var req: Filter<CandidateFilter, CandidateSearch> = getFilter(
			{ page: 0, size: totalCount } as Pagination,
			filter,
			search,
			sort
		);

		GetCandidates(
			req,
			(resp: ResponseContainer<ArrayContainer<CandidateTable>>) => {
				if (resp.status === "ok" && resp.data) {
					callback(resp.data.data);
				}
			}
		);
	};

	const getCandidates = () => {
		var req: Filter<CandidateFilter, CandidateSearch> = getFilter(
			pagination as Pagination,
			filter,
			search,
			sort
		);

		GetCandidates(
			req,
			(resp: ResponseContainer<ArrayContainer<CandidateTable>>) => {
				if (resp.status === "ok" && resp.data) {
					setCandidates(resp.data.data);
					setTotalCount(resp.data.total_count || 0);
				}
			}
		);
	};

	useEffect(() => {
		getCandidates();
	}, [filter, pagination, search, sort]);

	return (
		<div className="flex flex-col items-end gap-5">
			<div className="flex w-full justify-between">
				<Button
					onClick={() => {
						setOpenModal(true);
					}}
				>
					Создать
				</Button>
				<FilterBase setSearch={setSearch} setFilter={setFilter}>
					<DataTextField
						keyValue={"full_name"}
						value={search}
						setValue={setSearch}
						inputProps={{ label: "Поиск" }}
					/>
					<DataSelect
						keyValue={"id_manager"}
						list={managers}
						value={filter}
						setValue={setFilter}
						label="Отвественный"
					/>
					<PeriodSelector
						value={filter}
						setValue={setFilter}
						keyValue="created"
					/>
					<DataSelect
						keyValue={"status"}
						list={status}
						value={filter}
						setValue={setFilter}
						label="Статус"
					/>
					<DataSelect
						value={filter}
						keyValue={"gender"}
						list={GenderObjects}
						keyItem={"value"}
						keyTitle={"label"}
						setValue={setFilter}
						label="Пол"
					/>
				</FilterBase>
			</div>
			<CustomTable
				rows={rows}
				data={candidates}
				allCount={totalCount}
				pagination={pagination as Pagination}
				setPage={setPage as (p: number) => void}
				setPageSize={setPageSize as (p: number) => void}
				sort={sort}
				setSort={setSort}
				onExport={getCandidatesExport}
			/>
			<CreateCandidateModal
				open={openModal}
				onClose={() => setOpenModal(false)}
				onCreate={() => {
					getCandidates();
				}}
			/>
		</div>
	);
};

export default CandidatesPage;
